import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Clock, RotateCcw } from 'lucide-react';
import { cargarUltimoIntento, type ResultadoExamen } from './academyApi';
import { tituloNumerado } from './academyFormato';
import { EsqueletoAcademia } from './EsqueletoAcademia';
import { RevisionRespuestas } from './RevisionRespuestas';
import { scrollAlTope } from './scrollAlTope';
import { useCurso } from './useAcademia';
import { useEsperaExamen } from './useEsperaExamen';

/**
 * Las respuestas del ÚLTIMO examen rendido, fuera del momento de rendirlo
 * (ADR-2026-08-24-la-revision-sobrevive-al-intento).
 *
 * Quien desaprueba no tiene forma de mirar en qué se equivocó mientras espera las
 * dos horas —que es justamente cuando querría repasar—, así que el intento se
 * guarda y esta pantalla lo trae de vuelta.
 *
 * Con el examen en la base, el intento lo reconstruye `academy_ultimo_intento`:
 * vuelve a decidir del lado del servidor si revela las correctas, contra el
 * `aprobo` de ESE intento. El navegador no recibe lo que no le toca ver.
 */
export const PantallaRevision = () => {
  const { slug } = useParams<{ slug: string }>();
  const { cursos, curso, progreso, cargando } = useCurso(slug);
  const [intento, setIntento] = useState<ResultadoExamen | null>(null);
  const [buscando, setBuscando] = useState(true);

  useEffect(() => {
    if (!curso) return;
    let vivo = true;
    (async () => {
      try {
        const r = await cargarUltimoIntento(curso.id);
        if (vivo) setIntento(r);
      } catch (e) {
        console.error('[academia] no se pudo cargar el último intento', e);
      } finally {
        if (vivo) setBuscando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [curso]);

  if (cargando || (curso && buscando)) return <EsqueletoAcademia />;
  if (!curso || !progreso) return <Navigate to="/academia" replace />;
  // Sin intento guardado no hay nada que revisar.
  if (!intento) return <Navigate to={`/academia/${curso.slug}`} replace />;

  return (
    <Vista
      curso={curso}
      cursos={cursos}
      intento={intento}
      ultimoIntento={progreso.ultimoIntento}
    />
  );
};

function Vista({
  curso,
  cursos,
  intento,
  ultimoIntento,
}: {
  curso: NonNullable<ReturnType<typeof useCurso>['curso']>;
  cursos: ReturnType<typeof useCurso>['cursos'];
  intento: ResultadoExamen;
  ultimoIntento: number | null;
}) {
  const navigate = useNavigate();
  const raiz = useRef<HTMLDivElement>(null);
  const espera = useEsperaExamen(ultimoIntento, curso.esperaMinutos);

  useEffect(() => {
    scrollAlTope(raiz.current);
  }, []);

  return (
    <div ref={raiz} className="mx-auto max-w-content px-4 pb-24 pt-2 sm:px-8 md:pb-8">
      <div>
        {/* Mismo criterio que el back del curso: en mobile lo pone el shell
            (ADR-2026-08-25-en-mobile-el-back-es-el-del-shell). Desde `md`, que es
            donde el flotante desaparece. */}
        <button
          type="button"
          onClick={() => navigate(`/academia/${curso.slug}`)}
          className="mb-4 hidden items-center gap-1.5 text-[13.5px] font-medium text-black/50 transition-colors hover:text-brand-primary md:inline-flex"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al curso
        </button>

        <h1 className="text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black/90 sm:text-[32px]">
          Tus respuestas — {tituloNumerado(curso, cursos)}
        </h1>
      </div>

      <div className="mt-6">
        {/* Resumen del intento, en gris: acá el dato ya no es una novedad —el
            usuario viene a repasar, no a enterarse— así que el color se lo lleva
            la revisión de abajo, que es lo que vino a mirar. */}
        <div className="rounded-2xl border border-black/[0.06] bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-[15px] font-semibold text-brand-black/90">
                {intento.aprobo ? (
                  <>
                    <BadgeCheck className="h-4.5 w-4.5 text-emerald-600" strokeWidth={2} />
                    Aprobado con {intento.puntaje}%
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 text-amber-500" strokeWidth={2.2} />
                    No aprobado — {intento.puntaje}%
                  </>
                )}
              </p>
              <p className="mt-1 text-[13px] text-black/45">
                Último intento del {fechaLarga(intento.rendidoAt)} · Necesitás{' '}
                {curso.puntajeMinimo}% para aprobar
              </p>
            </div>

            {/* Rendir de nuevo vive también acá: quien mira en qué se equivocó es
                quien va a querer reintentar. Sólo si NO aprobó
                (ADR-2026-08-24-el-curso-aprobado-no-se-repite). */}
            {!intento.aprobo && (
              <button
                type="button"
                onClick={() => navigate(`/academia/${curso.slug}/examen`)}
                disabled={!espera.listo}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:bg-black/[0.08] disabled:text-black/35"
              >
                {espera.listo ? (
                  <>
                    <RotateCcw className="h-3.5 w-3.5" />
                    Rendir de nuevo
                  </>
                ) : (
                  <>
                    <Clock className="h-3.5 w-3.5" />
                    Disponible en{' '}
                    <span className="tabular-nums">{espera.etiqueta}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <RevisionRespuestas
          correcciones={intento.correcciones}
          titulo="Lo que respondiste"
        />
      </div>
    </div>
  );
}

/**
 * "24 de agosto, 15:32". Sin el año ni los segundos: el intento que se muestra es
 * el ÚLTIMO, así que casi siempre es reciente y el resto sólo suma ruido.
 */
function fechaLarga(iso: string): string {
  const d = new Date(iso);
  const dia = d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });
  const hora = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return `${dia}, ${hora}`;
}
